import React from "react";
import { useFormik } from "formik";
import { useMutation, useQuery, useQueryClient } from "react-query";
import * as Yup from "yup";
import { Modal, Button, Form } from "react-bootstrap";
import SubAccountPicker from "../SubAccount";
import { useAuth } from "../../../hooks/useAuth";
import { toast } from "react-toastify";

const EditSubAccountModal = ({ old, onHide, onSuccess, show, refetch }) => {
  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async values => {
      const response = await fetch(
        `${backendUrl}/api/accounts/change-sub-account`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...values, AccountID: old?.AccountID }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update sub account");
      }

      return response.json();
    },
    {
      onSuccess: () => {
        if (refetch) refetch();
        //
        toast.success("Sub account updated successfully");
        queryClient.invalidateQueries(["subaccounts"]);
        onSuccess?.();
        onHide?.();
      },
      onError: error => {
        console.error("Error updating sub account:", error);
        toast.error("Failed to update sub account. Please try again.");
      },
    }
  );

  const validationSchema = Yup.object({
    Type: Yup.string().required("Type is required"),
    // Add other validation fields as needed
  });

  // GET SUB ACCOUNT DATA TO POPULATE FORM
  const { data } = useQuery(
    ["subAccounts", old?.subAccountID],
    async () => {
      const response = await fetch(
        `${backendUrl}/api/accounts/accountLists/${old?.subAccountID}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch sub account data");
      }
      return response.json();
    },
    {
      enabled: !!old?.AccountID,
    }
  );

  const formik = useFormik({
    initialValues: {
      AccountID: old?.AccountID || "",
      subAccountID: old?.subAccountID || "",
      subAccountDescription: "",
      subAccountType: "",
      // Initialize other fields from old prop
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: values => {
      mutation.mutate(values);
    },
  });

  // Update form values when data loads
  React.useEffect(() => {
    if (data) {
      formik.setValues({
        AccountID: old.AccountID || "",
        subAccountID: old.subAccountID || "",
        Type: old.Type,

        // WE THIS FOR UI
        subAccountID: data.account?.AccountID || "",
        subAccountDescription: data.account?.Description || "",
        subAccountType: data.account?.Type || "",
      });
    }
  }, [data]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      centered
      size="md"
      backdropClassName="custom-backdrop"
      animation={false}
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="h5">Edit</Modal.Title>
      </Modal.Header>

      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <p className="h5">{old?.Description}</p>

          <SubAccountPicker
            formik={formik}
            label={
              ["FIXED ASSET", "NON-CURRENT ASSETS"].includes(formik.values.Type)
                ? "Accumulated Depreciation"
                : "..."
            }
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={mutation.isLoading}>
            {mutation.isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditSubAccountModal;
